import { useEffect, useState } from 'react';
import { Modal, Stack, Group, Badge, Alert, Button, Textarea, Text } from '@mantine/core';
import {
  IconArrowRight,
  IconAlertCircle,
  IconInfoCircle,
  IconLink,
  IconPlus,
} from '@tabler/icons-react';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect/RepositorySelect';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useLeadRepository, useLostReasonRepository } from '../../../../rpc';
import DealPickerModal from '../../../Deals/DealPickerModal';
import { CreateDealForm } from '../../../Deals/forms/CreateDealForm';
import type { LeadExtended } from '../LeadViewController';
import type { CreateDeal } from '../../../../../schemas/deal.schema';
import type { LeadStatus } from '../../../../../schemas/lead-status.schema';

export interface NegativeCloseExtras {
  lost_reason_id: number;
  lost_comment: string | null;
}

export interface PositiveCloseExtras {
  won_comment: string | null;
}

export interface LeadStatusChangeExtras {
  lost_reason_id?: number;
  lost_comment?: string | null;
  won_comment?: string | null;
  deal_id?: number;
  new_deal?: CreateDeal;
}

interface LeadStatusChangeModalProps {
  opened: boolean;
  leadId: number;
  currentStatus: LeadStatus | null | undefined;
  targetStatus: LeadStatus | null;
  error: string | null;
  changing: boolean;
  onConfirm: (extras?: LeadStatusChangeExtras) => void;
  onClose: () => void;
}

/** Форма перехода: требования и подсказка берутся из настроек целевого статуса. */
export function LeadStatusChangeModal({
  opened,
  leadId,
  currentStatus,
  targetStatus,
  error,
  changing,
  onConfirm,
  onClose,
}: LeadStatusChangeModalProps) {
  const lostReasonRepository = useLostReasonRepository();
  const leadRepository = useLeadRepository();
  const dealPicker = useModal(DealPickerModal);
  const dealForm = useModal(CreateDealForm);
  const [lostReasonId, setLostReasonId] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [lead, setLead] = useState<LeadExtended | null>(null);
  const [deal, setDeal] = useState<{ id: number; number?: string | null } | null>(null);
  const [newDeal, setNewDeal] = useState<CreateDeal | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const isNegative = targetStatus?.is_negative === true;
  const isPositive = targetStatus?.is_positive === true;
  const requireComment = targetStatus?.require_comment ?? isPositive;
  const requireDeal = targetStatus?.require_deal === true;
  const managerText = targetStatus?.manager_text?.trim();

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLostReasonId(null);
    setComment('');
    setLead(null);
    setDeal(null);
    setNewDeal(null);
    setLoadError(null);
    setLoading(requireDeal);
    if (requireDeal) {
      leadRepository
        .withRelations()
        .findById(leadId)
        .then(result => {
          if (cancelled) return;
          if (!result) throw new Error('Лид не найден');
          const loaded = result as LeadExtended;
          setLead(loaded);
          setDeal(loaded.deal_id ? { id: loaded.deal_id, number: loaded.deal?.number } : null);
        })
        .catch(err => {
          if (!cancelled)
            setLoadError(err instanceof Error ? err.message : 'Не удалось загрузить лид');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [opened, leadId, targetStatus?.id, requireDeal, leadRepository]);

  const handleConfirm = () => {
    if (changing || loading || loadError || (isNegative && !lostReasonId)) return;
    const trimmed = comment.trim();
    if (requireComment && !trimmed) return;
    if (requireDeal && !deal && !newDeal) return;
    const extras: LeadStatusChangeExtras = {};
    if (isNegative && lostReasonId != null) {
      extras.lost_reason_id = lostReasonId;
      extras.lost_comment = trimmed || null;
    } else if (isPositive || requireComment) {
      extras.won_comment = trimmed || null;
    }
    if (requireDeal && deal) extras.deal_id = deal.id;
    if (requireDeal && newDeal) extras.new_deal = newDeal;
    onConfirm(extras);
  };

  const openDealPicker = () => {
    dealPicker.open(
      {
        title: 'Привязать сделку',
        selectionMode: 'single',
        partnerId: lead?.partner_id,
        onDealSelected: deals => {
          if (deals[0]) {
            setDeal(deals[0]);
            setNewDeal(null);
          }
          dealPicker.close();
        },
        onClose: () => dealPicker.close(),
      },
      { size: 'xl' }
    );
  };

  const openDealForm = () => {
    if (!lead?.partner_id) return;
    dealForm.open(
      {
        title: 'Создать сделку',
        initialData: newDeal ?? {
          partner_id: lead.partner_id,
          deal_owner_id: lead.manager_id,
          amount: lead.amount,
          description: `Создана из лида: ${lead.title}`,
        },
        disablePartner: true,
        onPrepare: values => {
          setNewDeal(values);
          setDeal(null);
          dealForm.close();
        },
        onDealCreated: () => dealForm.close(),
      },
      { size: 'lg' }
    );
  };

  const confirmDisabled =
    loading ||
    !!loadError ||
    (isNegative && !lostReasonId) ||
    (requireComment && !comment.trim()) ||
    (requireDeal && !deal && !newDeal);
  const title = isNegative
    ? 'Закрыть лид с отказом'
    : isPositive
      ? 'Закрыть лид успешно'
      : 'Изменить статус лида';
  const confirmLabel = isNegative
    ? 'Закрыть с отказом'
    : isPositive
      ? 'Закрыть успешно'
      : 'Перевести';
  const zIndex = useSurfaceZIndex(opened);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size='sm'
      centered
      closeOnClickOutside={false}
      zIndex={zIndex}
    >
      <Stack gap='md'>
        <Group gap='sm' align='center'>
          <Badge color={currentStatus?.color ?? 'gray'} variant='light' size='md'>
            {currentStatus?.name ?? 'Не задан'}
          </Badge>
          <IconArrowRight size={16} color='var(--mantine-color-dimmed)' />
          {targetStatus && (
            <Badge color={targetStatus.color ?? 'gray'} variant='filled' size='md'>
              {targetStatus.name}
            </Badge>
          )}
        </Group>

        {managerText && (
          <Alert color='blue' variant='light' icon={<IconInfoCircle size={18} />}>
            <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
              {managerText}
            </Text>
          </Alert>
        )}

        {isNegative && (
          <RepositorySelect
            repo={lostReasonRepository.filterActive().orderBy('sort_order', 'asc').limit(100)}
            displayField='name'
            valueField='id'
            label='Причина отказа *'
            placeholder='Выберите причину'
            value={lostReasonId != null ? String(lostReasonId) : null}
            onChange={value => setLostReasonId(value ? parseInt(value, 10) : null)}
            clearable
            size='xs'
            disabled={changing}
          />
        )}

        {requireDeal && (
          <Stack gap='xs'>
            <Text size='sm' fw={500}>
              Сделка *
            </Text>
            {deal && <Text size='sm'>Сделка №{deal.number || deal.id}</Text>}
            {newDeal && (
              <Text size='sm'>Новая сделка будет создана при подтверждении перехода</Text>
            )}
            {!loading && !deal && !newDeal && (
              <Text size='xs' c='dimmed'>
                Выберите существующую сделку или создайте новую.
              </Text>
            )}
            {!lead?.deal_id && (
              <Group gap='xs'>
                <Button
                  size='xs'
                  variant='light'
                  leftSection={<IconLink size={14} />}
                  onClick={openDealPicker}
                  disabled={changing || loading || !!loadError}
                >
                  Привязать сделку
                </Button>
                <Button
                  size='xs'
                  variant='light'
                  leftSection={<IconPlus size={14} />}
                  onClick={openDealForm}
                  disabled={changing || loading || !lead?.partner_id || !!lead?.deal_id}
                >
                  Создать сделку
                </Button>
              </Group>
            )}
            {!loading && !loadError && !lead?.deal_id && !lead?.partner_id && (
              <Text size='xs' c='dimmed'>
                Для создания сделки сначала укажите партнёра в лиде.
              </Text>
            )}
          </Stack>
        )}

        {(isNegative || isPositive || requireComment) && (
          <Textarea
            label={requireComment ? 'Комментарий *' : 'Комментарий'}
            placeholder={requireComment ? 'Введите комментарий' : 'Комментарий (необязательно)'}
            value={comment}
            onChange={e => setComment(e.currentTarget.value)}
            autosize
            minRows={3}
            maxRows={10}
            size='xs'
            disabled={changing}
          />
        )}

        {(error || loadError) && (
          <Alert icon={<IconAlertCircle size={16} />} color='red' variant='light'>
            {error || loadError}
          </Alert>
        )}
        <Group justify='flex-end' gap='sm'>
          <Button variant='light' color='gray' size='xs' onClick={onClose} disabled={changing}>
            Отмена
          </Button>
          <Button
            size='xs'
            onClick={handleConfirm}
            loading={changing || loading}
            disabled={confirmDisabled}
            color={isNegative ? 'red' : isPositive ? 'green' : undefined}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
